import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ActionModal, { ActionModalComponentProps } from '../../components/Modal/ActionModal/ActionModal';
import { inMobileXL, useWindowDimensions } from '../../hooks/useDimensions';
import { StyledFilterButton } from '../../overwrites/components/SearchInput/FilterButton';
import { Input } from '../';
import {
  InlineWrapper,
  InputContainer,
  ShimmerSearchButtonWrapper,
  StyledSearchIcon,
  StyledSearchInput,
  StyledShimmerInputWrapper,
  StyledWrapper,
} from './SearchInput.styles';

interface SearchInputProps {
  loading?: boolean;
  searchText?: string;
  searchPlaceholder?: string;
  onSearchChange?: (text: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  focused?: boolean;
  fullWidth?: boolean;
  height?: string;
  renderModalExtra?: () => React.ReactNode;
  /** Always show the inline input, even on mobile (skip the button + modal flow) */
  forceInlineMode?: boolean;
}

const SearchInput = ({
  loading,
  searchText,
  searchPlaceholder,
  onSearchChange,
  onFocus,
  onBlur,
  focused,
  fullWidth,
  height,
  renderModalExtra,
  forceInlineMode,
}: SearchInputProps) => {
  const { width } = useWindowDimensions();
  const [searchTextLocal, setSearchTextLocal] = useState(searchText || '');
  const [isFocused, setIsFocused] = useState(false);
  const searchModalRef = useRef(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();

  // Sync local state with prop changes
  useEffect(() => {
    setSearchTextLocal(searchText || '');
  }, [searchText]);

  const onTextChange = (e: any) => {
    const text = e.target.value as string;
    setSearchTextLocal(text);
    onSearchChange?.(text);
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('[aria-label="clear"], .nextui-input-clear-button')) {
      return;
    }

    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
      onBlur?.();
    }, 150);
  };

  const onSearchClick = () => {
    searchModalRef.current.open();
  };

  const handleClearClick = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setSearchTextLocal('');
    onSearchChange?.('');
  };

  const SearchModalInput = forwardRef(({ onFinish }: ActionModalComponentProps, ref): any => {
    const [text, setText] = useState(searchTextLocal);

    useImperativeHandle(ref, () => ({
      submit: () => {
        setSearchTextLocal(text);
        onSearchChange?.(text);
        onFinish?.();
      },
    }));

    return (
      <InputContainer>
        <Input
          placeholder={t('common.search')}
          aria-label={t('common.search')}
          iconLeft={<StyledSearchIcon />}
          clearable
          onChange={(e: any) => setText(e.target.value as string)}
          value={text}
        />
        {renderModalExtra?.()}
      </InputContainer>
    );
  });

  if (forceInlineMode) {
    return (
      <InlineWrapper $fullWidth={fullWidth || isFocused}>
        <StyledSearchInput
          id="search"
          label=""
          placeholder={searchPlaceholder}
          name="search"
          aria-label={searchPlaceholder}
          clearable
          iconLeft={<StyledSearchIcon />}
          onChange={onTextChange}
          value={searchTextLocal}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClearClick={handleClearClick}
          $focused={isFocused || focused}
          $fullWidth={fullWidth}
          $height={height}
          $forceVisible
        />
      </InlineWrapper>
    );
  }

  return (
    <>
      <ShimmerSearchButtonWrapper loading={loading}>
        <StyledFilterButton
          icon={<StyledSearchIcon />}
          showBadge={searchTextLocal.length > 1}
          onClick={onSearchClick}
        />
      </ShimmerSearchButtonWrapper>
      <StyledShimmerInputWrapper loading={loading}>
        <StyledWrapper $fullWidth={fullWidth || isFocused} onClick={inMobileXL(width) ? onSearchClick : undefined}>
          <StyledSearchInput
            id="search"
            label=""
            placeholder={searchPlaceholder}
            name="search"
            aria-label={searchPlaceholder}
            clearable
            iconLeft={<StyledSearchIcon />}
            onChange={onTextChange}
            value={searchTextLocal}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClearClick={handleClearClick}
            $focused={isFocused || focused}
            $fullWidth={fullWidth}
            $height={height}
          />
        </StyledWrapper>
      </StyledShimmerInputWrapper>
      <ActionModal
        ref={searchModalRef}
        title={searchPlaceholder}
        submitButtonTitle={t('common.search')}
        Component={SearchModalInput}
        componentProps={{
          fullWidth,
          focused,
        }}
      />
    </>
  );
};

export default SearchInput;
